import React, { useRef, useState } from 'react';
import { JarallaxOptions } from 'jarallax';
import classnames from 'classnames';
import { Link } from 'react-router-dom';

import type { BannerType } from 'src/components/PageBuilder/components';
import { RichText } from 'src/components/RichText/RichText';
import { Button } from 'src/components/Button/Button';
import { useBreakpointMatch } from 'src/hooks/useBreakpoint';
import { getBackgroundSettings } from 'src/components/PageBuilder/components/backgroundSettings';
import { getAdvancedSettings } from 'src/components/PageBuilder/components/advancedSettings';
import { PageBuilderBannerProps } from 'src/components/PageBuilder/components';
import { useLazyBackgroundImage } from 'src/hooks/useLazyBackgroundImage';

import { useJarallax } from '../../hooks';

import classes from './Banner.module.scss';

type BannerProps = BannerType;

/**
 * Return the PB Banner
 * @param props
 * @param ref
 * @constructor
 */
export const Banner = React.forwardRef<HTMLDivElement, BannerProps>((props, ref) => {
    useLazyBackgroundImage({ uniqueId: props.uniqueID || '' });

    /**
     * Configure the styles requires for the Block
     */
    const backgroundStyles = getBackgroundSettings<PageBuilderBannerProps>({ ...props });
    const advancedSettings = getAdvancedSettings<PageBuilderBannerProps>(props);

    const layoutStyles = {
        minHeight: props?.minHeight ?? 0,
    };

    const [hovered, setHovered] = useState(false);
    const toggleHover = () => setHovered(!hovered);

    const { isDesk } = useBreakpointMatch();

    /**
     * Configure the styles requires for the Block
     */
    const internalRef = useRef<HTMLDivElement | null>(null);
    const backgroundElement = ref || internalRef;
    const hasVideoOverlay = props.videoOverlayColor !== undefined;
    const videoOverlayStyles = {
        backgroundColor: props.videoOverlayColor,
    };

    const hasOverlay = (props.overlayColor?.length ?? 0) > 0 && props?.showOverlay !== 'never';
    const overlayStyles = {
        backgroundColor: props.overlayColor,
    };

    if (props.showOverlay === 'hover') {
        overlayStyles.backgroundColor = 'transparent';
    }

    if (hovered && props.showOverlay === 'hover') {
        overlayStyles.backgroundColor = props.overlayColor;
    }

    /**
     * If the background type is video or parallax is enabled, we need to enable the parallax effect
     */
    const isParallaxEnabled = Boolean(props.backgroundType === 'video' || props.enableParallax);
    const parallaxSpeed = !props.enableParallax ? 1 : props.parallaxSpeed;

    // For jarallax, use the current breakpoint image, but only for video or when parallax is enabled
    const backgroundImageForJarallax = isDesk ? props.desktopImage : props.mobileImage;

    const config: JarallaxOptions = {
        imgPosition: props.backgroundPosition,
        imgRepeat: props.backgroundRepeat === 'no-repeat' ? 'no-repeat' : 'repeat',
        imgSize: props.backgroundSize,
        imgSrc: props.enableParallax ? backgroundImageForJarallax : undefined,
        speed: parallaxSpeed,
        videoLazyLoading: props.videoLazyLoading,
        videoLoop: props.videoLoop,
        videoPlayOnlyVisible: props.videoPlayOnlyVisible,
        videoSrc: props.videoSrc,
    };

    useJarallax({
        isEnabled: isParallaxEnabled,
        backgroundElement,
        config,
        backgroundType: props.backgroundType,
    });

    const bannerClassNames = ['pageBuilderBannerGlobal'];

    if (props.cssClasses && props.cssClasses.length > 0) {
        bannerClassNames.push(...props.cssClasses);
    }

    // Parse the button text and background colours from the props
    const buttonTextColour = props.buttonTextColour ? JSON.parse(props.buttonTextColour) : undefined;

    const buttonBackgroundColour = props.buttonBackgroundColour ? JSON.parse(props.buttonBackgroundColour) : undefined;

    const renderBanner = () => {
        // Check if we have background images to apply lozad
        const hasBackgroundImages = props.desktopImage || props.mobileImage;

        return (
            <div
                data-desktop-img={props.desktopImage}
                data-mobile-img={props.mobileImage}
                data-content-type={props.contentType}
                data-unique-id={props.uniqueID}
                id={props.uniqueID}
                ref={backgroundElement}
                style={{
                    ...backgroundStyles,
                    ...advancedSettings,
                    ...layoutStyles,
                }}
                className={classnames({
                    [bannerClassNames.join(' ')]: true,
                    ['pageBuilderBannerGlobal']: true,
                    [classes['root']]: true,
                    [classes['rootSlide']]: props.contentType === 'slide',
                    [classes['rootPoster']]: props.appearance === 'poster',
                    ['lozad']: hasBackgroundImages,
                    ['lozad-loading']: hasBackgroundImages,
                })}
                onMouseEnter={toggleHover}
                onMouseLeave={toggleHover}
                aria-label={props.altText}
                title={props.titleText}
            >
                {hasVideoOverlay && <div className={classes['videoOverlay']} style={videoOverlayStyles} />}
                {props.appearance === 'poster' && <div className={classes['videoOverlay']} style={overlayStyles} />}
                <div
                    className={classnames({
                        [classes['container']]: true,
                        [classes['containerLeft']]: props.appearance === 'collage-left',
                        [classes['containerRight']]: props.appearance === 'collage-right',
                        [classes['containerCentered']]: props.appearance === 'collage-centered',
                    })}
                >
                    {props.content && (
                        <div
                            className={classnames({
                                pageBuilderBannerContentGlobal: true,
                                [classes['content']]: true,
                                [classes['contentBlank']]: !hasOverlay,
                                [classes['contentPoster']]: props.appearance === 'poster',
                                [classes['contentLeft']]: props.appearance === 'collage-left',
                                [classes['contentRight']]: props.appearance === 'collage-right',
                                [classes['contentCentered']]: props.appearance === 'collage-centered',
                            })}
                            style={props.showOverlay !== 'never' && props.appearance !== 'poster' ? overlayStyles : {}}
                        >
                            <RichText content={props.content} />

                            {props.linkType && props.showButton !== 'never' && props.buttonText && (
                                <Button
                                    className={classnames({
                                        [classes['contentButton']]: true,
                                        [classes['buttonHover']]: props.showButton === 'hover',
                                    })}
                                    style={{
                                        backgroundColor: buttonBackgroundColour,
                                        color: buttonTextColour,
                                    }}
                                    variant={props.buttonType}
                                >
                                    {props.buttonText}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            {!props.link && renderBanner()}

            {props.link && (
                <Link
                    to={props.link ?? '/'}
                    target={props.openInNewTab ? '_blank' : ''}
                    className={classes['link']}
                    aria-label={props.altText || 'Banner Link'}
                >
                    {renderBanner()}
                </Link>
            )}
        </>
    );
});

Banner.displayName = 'Banner';
